import { type Except, ReactUtils } from '@festina-life/core'
import { NavLink } from '@festina-life/ui/actions'
import { Circle } from '@visx/shape'
import cx from 'classnames'
import type { ComponentProps } from 'react'

import { useChangesetStateContext } from '../../context/changesetStateContext'
import { useConfigurationContext } from '../../context/configurationContext'
import {
  ACTIVE_DOT_STROKE,
  DOT_RADIUS,
  INACTIVE_DOT_STROKE,
  INHERITED_DOT_RADIUS,
} from '../versionedTimeline/constants'
import type { VersionLinkWithViewDate } from './types'

type Props = {
  isActive: boolean
  isDraft: boolean
  isInherited: boolean
  isHideVersion?: boolean
  isBeginningOfTime?: boolean
  to?: VersionLinkWithViewDate
} & Except<ComponentProps<typeof Circle>, 'to'>

export const OperationVersionCircle = ({
  isActive,
  isDraft,
  isInherited,
  isHideVersion = false,
  isBeginningOfTime = false,
  to,
  ...other
}: Props) => {
  const { onViewAtChanged } = useConfigurationContext()
  const { onReset } = useChangesetStateContext()
  const radius = isInherited ? INHERITED_DOT_RADIUS : DOT_RADIUS

  return (
    <ReactUtils.ConditionalWrap
      condition={!!to}
      wrap={(children) => (
        <NavLink
          // We bang because we know `to` is defined if this wrap is used
          // and given that we're inside a function, the expression isn't evaluated beforehand
          to={to!.path}
          onClick={() => {
            if (to?.atTime) {
              const targetDate = new Date(to.atTime.getTime())
              if (isBeginningOfTime) {
                targetDate.setDate(targetDate.getDate() - 1)
              }
              onViewAtChanged(targetDate)
            }
            if (isDraft) {
              // Clicking on a draft should reset the changeset hiding to show all changesets
              onReset()
            }
          }}
        >
          {children}
        </NavLink>
      )}
    >
      <Circle
        r={isActive || isBeginningOfTime ? radius : radius - ACTIVE_DOT_STROKE}
        strokeWidth={
          isActive || isBeginningOfTime
            ? ACTIVE_DOT_STROKE
            : INACTIVE_DOT_STROKE
        }
        className={cx('z-1 hover:opacity-80', {
          'stroke-timeline-dotActive': isActive,
          'stroke-amaze-ink dark:stroke-amaze-inkBright': !isActive,
          'fill-timeline-dot': !isDraft && !isBeginningOfTime && !isHideVersion,
          'fill-draft-600 dark:fill-draft-400':
            isDraft && !isHideVersion && !isBeginningOfTime,
          'fill-slate-100 dark:fill-amaze-paperDark': isHideVersion,
          'cursor-pointer': !isDraft && !!to,
          'fill-transparent': isBeginningOfTime && !isHideVersion,
        })}
        {...other}
      />
    </ReactUtils.ConditionalWrap>
  )
}
